import React, { useRef, useState } from 'react';
import { SlaDeltaStripCell, SlaDeltaStripLeg } from '../../../interfaces/panel-data';
import { formatDateFromDate } from '../../../utils/date-helper';
import { formatDuration } from '../../../utils/format-duration';
import { VERDICT_LABELS } from './sla-verdict-labels';
import './DeltaStrip.scss';

export type DeltaStripCell = SlaDeltaStripCell;

/** Just enough of the tier's group row to say that a leg is expected and what to call it. */
export interface DeltaStripLegName {
  key: string;
  displayName: string;
}

/** How wide the tooltip is drawn. Fixed, so its anchor can be clamped inside the strip. */
const TOOLTIP_WIDTH = 430;

interface DeltaStripProps {
  cells: DeltaStripCell[];
  /**
   * The legs the tier lists, in its order. Used to name a leg a cell has no entry for, which is the difference
   * between *not started yet* and *never started*.
   */
  legNames: DeltaStripLegName[];
  /** Rendered only when the cap withheld cells — see `isCapped`. */
  capNote: string | undefined;
  /** How many cells the backend was willing to send, which is what makes truncation detectable. */
  cap: number;
}

const plural = (count: number, noun: string): string =>
  `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;

/** Errored, or due with no workflows at all — the two things that make a cell red. */
const isBroken = (leg: SlaDeltaStripLeg): boolean =>
  !leg.triggered || leg.verdict === 'FAILED' || leg.state === 'FAILED';

/**
 * Green once every expected leg has succeeded, red as soon as one errored or was due and started nothing, and
 * neutral in between — still running is neither a success nor a failure.
 *
 * An outcome, not a verdict: a leg can succeed far outside its budget and still be green here, which is why
 * the key says so and the band bars above keep the judging.
 */
const cellOutcome = (
  cell: DeltaStripCell,
  legNames: DeltaStripLegName[]
): 'succeeded' | 'failed' | 'in-flight' => {
  if (cell.legs.some(isBroken)) {
    return 'failed';
  }
  const everyLegRan =
    legNames.every((expected) => cell.legs.some((leg) => leg.leg === expected.key)) &&
    cell.legs.every((leg) => leg.state === 'SUCCEEDED');
  return everyLegRan ? 'succeeded' : 'in-flight';
};

/** One leg's block of the tooltip, in the fields it is laid out by rather than as a sentence. */
interface LegDetail {
  name: string;
  /** State and clock together: `SUCCEEDED · ran 1d 16h of budget 24h`. */
  status: string;
  /** Its own line, because the ids are long and are the thing an operator copies out. */
  workflows: string | null;
  verdict: string | null;
  /** Errored or never started, so the block is drawn in the same red the cell is. */
  bad: boolean;
  /** Never started. Marked as well as coloured: the cell's colour carries only its worst leg. */
  missing: boolean;
}

const legDetail = (leg: SlaDeltaStripLeg, name: string): LegDetail => {
  if (!leg.triggered) {
    const overdue = leg.elapsedMs === null ? '' : `, overdue ${formatDuration(leg.elapsedMs)}`;
    return {
      name,
      status: `never triggered — no workflows at all${overdue}`,
      workflows: null,
      verdict: null,
      bad: true,
      missing: true,
    };
  }
  const ran = leg.elapsedMs === null ? 'no runtime recorded' : `ran ${formatDuration(leg.elapsedMs)}`;
  // Not "no budget configured": the budget exists and the panel declined to compare against it, which is the
  // opposite claim. The runtime is still shown — a leg can burn nineteen hours before erroring.
  const spent =
    leg.verdict === 'FAILED'
      ? `${ran}, not judged against its budget`
      : `${ran} of ${
          leg.budgetMs === null ? 'no budget configured' : `budget ${formatDuration(leg.budgetMs)}`
        }`;
  return {
    name,
    status: `${leg.state ?? 'state unknown'} · ${spent}`,
    workflows:
      leg.workflowIds === null
        ? plural(leg.workflowCount ?? 0, 'workflow')
        : `${plural(leg.workflowCount ?? 0, 'workflow')}: ${leg.workflowIds}`,
    verdict: VERDICT_LABELS[leg.verdict],
    bad: isBroken(leg),
    missing: false,
  };
};

/**
 * Every leg the tier knows about, whether the delta has one or not, so a cell answers "did SCI and DTP run?"
 * rather than by omission; an unlisted leg is still described at the end, because dropping it would read as
 * "not due yet". The tier's name wins over the leg's own, which keeps `DTP Delta` off `DTP_DELTA`.
 */
const legDetails = (cell: DeltaStripCell, legNames: DeltaStripLegName[]): LegDetail[] => {
  const found = new Map(cell.legs.map((leg) => [leg.leg, leg]));
  const named = legNames.map((expected) => {
    const leg = found.get(expected.key);
    return leg === undefined
      ? {
          name: expected.displayName,
          status: 'not due yet',
          workflows: null,
          verdict: null,
          bad: false,
          missing: false,
        }
      : legDetail(leg, expected.displayName);
  });
  const unexpected = cell.legs
    .filter((leg) => !legNames.some((expected) => expected.key === leg.leg))
    .map((leg) => legDetail(leg, leg.displayName));
  return [...named, ...unexpected];
};

/** The same content as one line, for the cell's accessible name — a hover tooltip has no screen reader. */
const legText = (detail: LegDetail): string =>
  [
    `${detail.missing ? '✗ ' : ''}${detail.name}: ${detail.status}`,
    detail.workflows,
    detail.verdict,
  ]
    .filter((part) => part !== null)
    .join(' · ');

const cellLabel = (cell: DeltaStripCell, legNames: DeltaStripLegName[]): string =>
  [
    `${cell.id} · created ${formatDateFromDate(new Date(cell.createdAtMs))} · ${
      VERDICT_LABELS[cell.verdict]
    }`,
    ...legDetails(cell, legNames).map(legText),
  ].join('\n');

/** Which cell the tooltip is open for, and where in the strip to draw it. */
interface Hover {
  cell: DeltaStripCell;
  left: number;
  top: number;
}

/**
 * One website's EPC deltas, one cell each, in creation order — the shape of the chain over time. Sorted here
 * because the aggregation returns them newest first so the cap drops the oldest; drawn in the payload's order
 * the strip would tell the story backwards.
 *
 * Nothing is rendered without cells: an empty bordered box asserts a chain is empty, which is
 * indistinguishable from one nobody has indexed yet.
 */
export const DeltaStrip: React.FC<DeltaStripProps> = ({ cells, legNames, capNote, cap }) => {
  const cellsRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);

  if (cells.length === 0) {
    return null;
  }

  const ordered = [...cells].sort((left, right) => left.createdAtMs - right.createdAtMs);
  const oldest = ordered[0];
  const newest = ordered[ordered.length - 1];
  // No pre-cap total is on the wire, so a full strip is treated as a partial one: proving truncation before
  // saying so would mean never saying so.
  const isCapped = cells.length >= cap;
  // From the legs, not the cell verdicts: one cell can be missing both, and a reader chasing the colour is
  // looking for workflows to start rather than for squares.
  const untriggered = ordered.flatMap((cell) => cell.legs.filter((leg) => !leg.triggered));
  // Same reason, and counted at all because a failed leg appears nowhere else — off the band bars by design
  // and not an exception row. Without this a website whose every leg errored renders colour and no number.
  const failed = ordered.flatMap((cell) => cell.legs.filter((leg) => leg.verdict === 'FAILED'));

  /**
   * Clamped to the strip, so a cell on the right edge does not open a tooltip that runs off the panel. One
   * tooltip for the whole strip rather than one per cell, which is what keeps several hundred cells cheap.
   */
  const openTooltip = (cell: DeltaStripCell, target: HTMLElement) => {
    const rightmost = (cellsRef.current?.clientWidth ?? TOOLTIP_WIDTH) - TOOLTIP_WIDTH;
    setHover({
      cell,
      left: Math.max(0, Math.min(target.offsetLeft, rightmost)),
      top: target.offsetTop + target.offsetHeight + 6,
    });
  };

  return (
    <div className="ovp-dstrip">
      {/* In place of the clock sentence that used to sit here unlabelled, where it read as a caption on
          whichever side the reader had looked at last. Everything under this heading is an outcome;
          everything above it is a budget comparison. */}
      <h4 className="ovp-dstrip__heading" data-testid="sla-delta-strip-heading">
        Each delta&apos;s outcome, oldest first
      </h4>

      <div
        className="ovp-dstrip__cells"
        ref={cellsRef}
        role="img"
        aria-label={`${ordered.length.toLocaleString()} deltas in creation order, coloured by outcome`}
        data-testid="sla-delta-strip"
        onMouseLeave={() => setHover(null)}
      >
        {ordered.map((cell) => {
          const outcome = cellOutcome(cell, legNames);
          return (
            <span
              key={cell.id}
              className={`ovp-dstrip__cell ovp-dstrip__cell--${outcome}`}
              aria-label={cellLabel(cell, legNames)}
              data-testid={`delta-cell-${outcome}`}
              onMouseEnter={(event) => openTooltip(cell, event.currentTarget)}
            />
          );
        })}

        {/* Hidden from assistive technology on purpose: the same text is the cell's accessible name. Left
            interactive rather than `pointer-events: none`, so the workflow ids can be copied. */}
        {hover !== null && (
          <div
            className="ovp-dstrip__tip"
            style={{ left: `${hover.left}px`, top: `${hover.top}px` }}
            aria-hidden="true"
            data-testid="sla-delta-strip-tooltip"
          >
            <p className="ovp-dstrip__tip-head">
              <span className="ovp-dstrip__tip-id">{hover.cell.id}</span>
              <span className="ovp-dstrip__tip-verdict">{VERDICT_LABELS[hover.cell.verdict]}</span>
            </p>
            <p className="ovp-dstrip__tip-created">
              {`created ${formatDateFromDate(new Date(hover.cell.createdAtMs))}`}
            </p>
            {legDetails(hover.cell, legNames).map((detail) => (
              <div
                key={detail.name}
                className={`ovp-dstrip__tip-leg${detail.bad ? ' ovp-dstrip__tip-leg--bad' : ''}`}
              >
                <p className="ovp-dstrip__tip-leg-head">
                  <span className="ovp-dstrip__tip-leg-name">
                    {detail.missing ? `✗ ${detail.name}` : detail.name}
                  </span>
                  {detail.verdict !== null && (
                    <span className="ovp-dstrip__tip-leg-verdict">{detail.verdict}</span>
                  )}
                </p>
                <p className="ovp-dstrip__tip-leg-status">{detail.status}</p>
                {detail.workflows !== null && (
                  <p className="ovp-dstrip__tip-leg-ids">{detail.workflows}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* The key has to say that green is an outcome, or a cell that succeeded two days over its budget
          reads as a pass. */}
      <p className="ovp-dstrip__key" data-testid="sla-delta-strip-key">
        <span className="ovp-dstrip__swatch ovp-dstrip__swatch--succeeded" aria-hidden="true" />
        every leg succeeded
        <span className="ovp-dstrip__swatch ovp-dstrip__swatch--failed" aria-hidden="true" />
        a leg errored or never started
        <span className="ovp-dstrip__swatch ovp-dstrip__swatch--in-flight" aria-hidden="true" />
        still running
      </p>
      <p className="ovp-dstrip__note">
        Outcome only — whether a leg met its budget is what the bars above measure.
      </p>

      {/* Both counts are of legs, because a leg is not reachable from the colour alone: one delta can be
          missing or fail either leg, or both. */}
      {untriggered.length > 0 && (
        <p className="ovp-dstrip__note" data-testid="sla-delta-strip-missing-note">
          {`Never triggered: ${plural(untriggered.length, 'leg')} due with no workflows at all.` +
            ' Hover a cell for which delta and which leg.'}
        </p>
      )}

      {failed.length > 0 && (
        <p className="ovp-dstrip__note" data-testid="sla-delta-strip-failed-note">
          {`Failed: ${plural(failed.length, 'leg')} errored, not counted against any budget.` +
            ' Hover a cell for which delta and which leg.'}
        </p>
      )}

      {/* Identical cells say nothing about the span they cover, and 500 deltas are a very different window
          on a busy website than on a quiet one. */}
      <div className="ovp-dstrip__axis">
        <span>{`${oldest.id} · ${formatDateFromDate(new Date(oldest.createdAtMs))}`}</span>
        <span className="ovp-dstrip__axis-span">
          {`${ordered.length.toLocaleString()} deltas over ${formatDuration(
            Math.max(0, newest.createdAtMs - oldest.createdAtMs)
          )}`}
        </span>
        <span>{`${newest.id} · ${formatDateFromDate(new Date(newest.createdAtMs))}`}</span>
      </div>

      {isCapped && capNote && (
        <p className="ovp-dstrip__note" data-testid="sla-delta-strip-cap-note">
          {capNote}
        </p>
      )}
    </div>
  );
};
